(ns pfo.chat.feedback-component
  "Thumbs up/down on AARK replies (#3029). Nothing is stored until Send; a
  submitted thumb is filled, and clicking it again removes the rating."
  (:require [reagent.core :as r :refer [atom] :refer-macros [with-let]]
            ["react-dom" :as react-dom]
            [pfo.util :as util]
            [pfo.controller :as controller]
            [pfo.chat.model :as model]
            [pfo.widget.icons :as icons]))

(def ^:private disclosure
  ;; The issue says the final consent copy is still awaited; this is the prototype text.
  "Attaches this thread's messages and the records AARK™ referenced. Visible to the iPaladin review team only. Never used to train AI models and never shared outside iPaladin.")

(def ^:private copy
  {"positive" {:title "What worked well?"
               :placeholder "e.g. the trustee summary matched the deed exactly"
               :include "Include this conversation with your feedback"}
   "negative" {:title "What was wrong with this response?"
               :placeholder "e.g. the co-trustee appointed 6/2/22 was omitted"
               :include "Include this conversation to help us investigate"}})

(defn- new-draft [rating]
  {:rating rating
   :reasons #{}
   :detail ""
   :include-conversation (= rating "negative")})

(defn- sendable? [{:keys [rating reasons]}]
  (or (= rating "positive") (seq reasons)))

(def ^:private thumbs
  {"positive" ["Good response" icons/thumbs-up]
   "negative" ["Bad response" icons/thumbs-down]})

(def ^:private status-ms 1000)

(def ^:private status-text
  {:success "Feedback sent."
   :removed "Feedback removed."})

(defn- error-text
  "A rejected rating carries the server's reason; anything else gets the generic text."
  [error-response]
  (or (when (= 400 (:status error-response))
        (get-in error-response [:response :body :message]))
      "Failed to send feedback."))

(def ^:private popover-gap 8)

(defn- popover-position
  "Viewport coordinates next to the thumbs: above them, or below when the space
  above cannot hold the popover."
  [anchor-rect height]
  (if (< (- (.-top anchor-rect) height popover-gap) 0)
    {:left (.-left anchor-rect) :top (+ (.-bottom anchor-rect) popover-gap)}
    {:left (.-left anchor-rect) :bottom (+ (- (.-innerHeight js/window) (.-top anchor-rect)) popover-gap)}))

(defn- popover
  "The draft lives in the parent, so a failed send keeps every field for a retry."
  [{:keys [*draft *sending *removing *error *anchor *row on-send on-close]}]
  (let [*node (volatile! nil)
        *position (atom nil)
        place (fn [_]
                (when-let [anchor @*anchor]
                  (reset! *position (popover-position (.getBoundingClientRect anchor)
                                                      (some-> @*node .-offsetHeight)))))
        ;; The own thumbs toggle the popover themselves; anything else, a thumb of
        ;; another reply included, closes it. Tabbing out closes it too.
        outside? (fn [e] (not (some #(some-> % (.contains (.-target e))) [@*node @*row])))
        close-on-outside (fn [e] (when (outside? e) (on-close false)))
        close-on-escape (fn [e] (when (= "Escape" (.-key e)) (on-close true)))]
    (r/create-class
     {:component-did-mount
      (fn [_]
        (place nil)
        (.addEventListener js/document "mousedown" close-on-outside)
        (.addEventListener js/document "focusin" close-on-outside)
        (.addEventListener js/document "keydown" close-on-escape)
        (.addEventListener js/window "scroll" place true)
        (.addEventListener js/window "resize" place)
        ;; Keyboard users land on the first field: a reason chip, else the details.
        (some-> @*node (.querySelector ".c-message-feedback__reason, textarea") .focus))
      :component-will-unmount
      (fn [_]
        (.removeEventListener js/document "mousedown" close-on-outside)
        (.removeEventListener js/document "focusin" close-on-outside)
        (.removeEventListener js/document "keydown" close-on-escape)
        (.removeEventListener js/window "scroll" place true)
        (.removeEventListener js/window "resize" place))
      :reagent-render
      (fn [_]
        (let [{:keys [rating detail include-conversation] :as draft} @*draft
              selected-reasons (:reasons draft)
              {:keys [title placeholder include]} (copy rating)
              negative (= rating "negative")
              sending @*sending
              ;; Not while the earlier rating is being removed: the DELETE could erase this one.
              ready (and (sendable? draft) (not sending) (not @*removing))]
          [:div.c-message-feedback__popover {:role "dialog"
                                             :aria-label title
                                             ;; Transparent until placed; still focusable, unlike visibility: hidden.
                                             :style (or @*position {:opacity 0})
                                             :ref #(vreset! *node %)}
           [:div.c-message-feedback__header
            [:span.c-message-feedback__title title
             (when negative [:span.c-message-feedback__required " *"])]
            [:button.c-message-feedback__close {:type "button"
                                                :aria-label "Close"
                                                :on-click (util/handler #(on-close true))} "×"]]
           (when negative
             [:div.c-message-feedback__reasons {:role "group" :aria-label "Reason"}
              (for [[value label] model/feedback-reasons
                    :let [selected (contains? selected-reasons value)]]
                ^{:key value}
                [:button {:type "button"
                          :class (util/classes "c-message-feedback__reason" (when selected "is-selected"))
                          :aria-pressed selected
                          :on-click (util/handler
                                     #(swap! *draft update :reasons (if selected disj conj) value))}
                 label])])
           [:label.c-message-feedback__label
            "Additional details (optional)"
            [:span.c-message-feedback__helper "Don't include account numbers, SSNs, or other sensitive information"]
            [:textarea.c-message-feedback__detail {:value detail
                                                   :placeholder placeholder
                                                   :max-length 4000
                                                   :on-change #(swap! *draft assoc :detail (.. % -target -value))}]]
           [:label.c-message-feedback__include
            [:input {:type "checkbox"
                     :checked include-conversation
                     :on-change #(swap! *draft assoc :include-conversation (.. % -target -checked))}]
            [:span
             [:span.c-message-feedback__include-title include]
             [:span.c-message-feedback__helper disclosure]]]
           (when @*error
             [:div.c-message-feedback__error {:role "alert"} @*error])
           [:div.c-message-feedback__actions
            [util/disable-tool {:disabled sending
                                :class :u-non-clickable
                                :component [:button.c-message-feedback__discard {:type "button" :on-click (util/handler #(on-close true))}
                                            "Discard"]}]
            [util/disable-tool {:disabled (not ready)
                                :class :u-non-clickable
                                :component [:button.c-button {:type "button"
                                                              :on-click (util/handler #(when ready (on-send)))}
                                            (if sending "Sending…" "Send feedback")]}]]]))})))

(defn- thumb
  "One thumb: a button, or in a completed chat (READ-ONLY) a plain mark of the
  rating given. Filled once stored; an open popover darkens the outline like hover."
  [{:keys [rating submitted active read-only on-click]}]
  (let [[label icon] (thumbs rating)
        class (util/classes "c-message-feedback__thumb"
                            (str "c-message-feedback__thumb--" rating)
                            (when submitted "is-rated"))
        glyph [icon {:width 16 :height 16} submitted]]
    (if read-only
      [:span {:class class :role "img" :aria-label label} glyph]
      [:button {:type "button"
                :class class
                :aria-label (if submitted (str "Remove " label) label)
                :aria-pressed submitted
                :aria-expanded active
                :on-click on-click}
       glyph])))

(defn feedback-row
  "Thumbs under an AARK reply. LATEST keeps them visible; on older replies CSS
  shows them on hover or keyboard focus within the message. The popover stays
  open while it sends; on success it closes and \"Feedback sent.\" shows briefly
  next to the thumbs, on failure it stays open with the error and every field.
  READ-ONLY (a completed chat) shows only the rating already given."
  [message-obj {:keys [latest read-only]}]
  (with-let [*submitted (atom (get-in message-obj [:feedback :rating]))
             ;; The open popover's draft; nil while closed.
             *draft (atom nil)
             *sending (atom false)
             ;; A removal in flight: a send now could land before the DELETE and be erased by it.
             *removing (atom false)
             *status (atom nil)
             *error (atom nil)
             *status-timer (volatile! nil)
             ;; The thumb that opened the popover: where it is placed and where focus returns.
             *anchor (volatile! nil)
             ;; The thumbs row: clicks and focus inside it are not outside the popover.
             *row (volatile! nil)]
    (let [message-id (:id message-obj)
          open-rating (:rating @*draft)
          show-status (fn [type]
                        (some-> @*status-timer js/clearTimeout)
                        (reset! *status type)
                        (vreset! *status-timer (js/setTimeout #(reset! *status nil) status-ms)))
          ;; Keyboard users go back to the thumb; an outside click keeps focus where the user clicked.
          refocus #(some-> @*anchor .focus)
          ;; Not while sending: the outcome belongs to the open popover.
          dismiss (fn [refocus?]
                    (when-not @*sending
                      (when refocus? (refocus))
                      (reset! *draft nil)
                      (reset! *error nil)))
          ;; Guarded: a second click can land before the disabled button renders.
          send (fn []
                 (when-not (or @*sending @*removing)
                   (let [{:keys [rating reasons detail include-conversation]} @*draft]
                     (reset! *sending true)
                     (reset! *error nil)
                     (controller/dispatch :chat-feedback :save
                                          {:message-id message-id
                                           :feedback {:rating rating
                                                      :reasons (vec reasons)
                                                      :detail detail
                                                      :include_conversation include-conversation}
                                           :on-success (fn [_]
                                                         (refocus)
                                                         (reset! *submitted rating)
                                                         (reset! *sending false)
                                                         (reset! *draft nil)
                                                         (show-status :success))
                                           ;; The popover stays open with every field, so the user can retry at once.
                                           :on-error (fn [e]
                                                       (reset! *sending false)
                                                       (reset! *error (error-text e)))}))))
          click (fn [rating e]
                  (vreset! *anchor (.-currentTarget e))
                  (cond
                    (or @*sending @*removing) nil
                    (= rating @*submitted)
                    (do (reset! *removing true)
                        (controller/dispatch :chat-feedback :remove
                                             {:message-id message-id
                                              :on-success (fn [_]
                                                            (reset! *removing false)
                                                            (reset! *submitted nil)
                                                            (show-status :removed))
                                              :on-error (fn [_] (reset! *removing false))}))
                    (= rating open-rating) (dismiss false)
                    :else (reset! *draft (new-draft rating))))
          thumb-props (fn [rating]
                        {:rating rating
                         :submitted (= rating @*submitted)
                         :active (= rating open-rating)
                         :on-click #(click rating %)})
          ;; Blocked while a removal runs, so a new rating cannot race the DELETE.
          thumb-tool (fn [rating]
                       [util/disable-tool {:disabled @*removing
                                           :class :u-non-clickable
                                           :component [thumb (thumb-props rating)]}])]
      (if read-only
        (when-let [rating @*submitted]
          [:div.c-message-feedback.c-message-feedback--visible
           [thumb {:rating rating :submitted true :read-only true}]])
        [:div {:class (util/classes "c-message-feedback"
                                    (when (or latest @*draft @*status @*submitted) "c-message-feedback--visible"))
               :ref #(vreset! *row %)}
         (when @*draft
           ;; In a portal on body: the message list clips overflow and a transformed
           ;; ancestor would turn position: fixed relative to itself.
           (react-dom/createPortal
            (r/as-element [popover {:*draft *draft :*sending *sending :*removing *removing :*error *error :*anchor *anchor :*row *row
                                    :on-send send :on-close dismiss}])
            js/document.body))
         ;; Called, not rendered as [thumb-tool …]: a fn made in render is a new
         ;; component type each time, and React would remount the buttons.
         (thumb-tool "positive")
         (thumb-tool "negative")
         ;; Always rendered, so screen readers announce the text when it appears.
         [:span.c-message-feedback__status {:role "status"} (status-text @*status)]]))
    (finally
      (some-> @*status-timer js/clearTimeout))))
